import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { FC, useCallback } from 'react';
import { Category } from 'shared/api';
import { CONDITION_DESKTOP, CONDITION_MOBILE } from 'shared/common/constants';
import { routes } from 'shared/common/routes';
import { buildRoute, chooseEndingWord } from 'shared/helpers';
import styled, { CSSObject } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

type ListItem = {
  id: number;
  title: string;
  href: string;
  count: number;
  subCategories: Category[];
};

type RubricProps = {
  title: string;
  iconUrl?: string;
  href?: string;
  list: ListItem[];
  count: number;
  isUpcoming?: boolean;
};

type ListStyles = {
  ul: CSSObject;
  li: CSSObject;
};

const depthStylesMapping: Record<number, ListStyles> = {
  2: {
    li: { fontSize: '20px', fontWeight: 'bold', fontStretch: 'normal', fontStyle: 'normal', lineHeight: 1 },
    ul: { rowGap: '20px' },
  },
  3: {
    li: { fontSize: '16px', fontWeight: 500, fontStretch: 'normal', fontStyle: 'normal', lineHeight: 1.25 },
    ul: { rowGap: '20px' },
  },
  4: {
    li: { fontSize: '14px', fontWeight: 500, fontStretch: 'normal', fontStyle: 'normal', lineHeight: 1.07 },
    ul: { rowGap: '15px' },
  },
};

const getSubCategoriesCount = (subCategoriesArray: Category[]) =>
  subCategoriesArray.reduce((acc, item) => acc + item.expertsCount, 0);

export const TopRubric: FC<RubricProps> = ({ title, list, count }) => {
  const { t } = useTranslation('utils');

  const renderChildren = useCallback(
    (list: Category[], depthLevel: number) => (
      <CategoriesList
        style={{ ...depthStylesMapping[depthLevel]?.ul }}
        $isLastLevel={depthLevel === 4 || depthLevel === 5}
      >
        {list.map((item) => {
          const totalCountExperts = item.subCategories.reduce(
            (acc, item) => item.expertsCount + acc,
            item.expertsCount,
          );
          return (
            // isUpcoming ? (
            //   <ListItemWrapper $isUpcoming key={item.id}>
            //     {item.name}
            //   </ListItemWrapper>
            // ) : (
            <>
              <Link key={uuidv4()} href={`${buildRoute(routes.category, { id: item.id })}?mainCategory=${title}`}>
                <CategoriesListItem
                  style={{ ...depthStylesMapping[depthLevel]?.li, width: item.subCategories.length ? '100%' : '50%' }}
                >
                  <ListTitleWrapper>
                    <ListItemTitle>{item.name}</ListItemTitle>
                    <ListItemCount>{totalCountExperts}</ListItemCount>
                  </ListTitleWrapper>
                  {!!item.subCategories.length && renderChildren(item.subCategories, depthLevel + 1)}
                </CategoriesListItem>
              </Link>
            </>
          );
          // );
        })}
      </CategoriesList>
    ),
    [],
  );

  const renderChildrenMobile = (list: Category[], depthLevel: number) => (
    <MobileUl style={{ ...depthStylesMapping[depthLevel]?.ul }}>
      {list.map((item) => {
        const totalCountExperts = item.subCategories.reduce((acc, item) => item.expertsCount + acc, item.expertsCount);

        return (
          // isUpcoming ? (
          //   <ListItemWrapper $isUpcoming key={item.id}>
          //     {item.name}
          //   </ListItemWrapper>
          // ) : (
          <>
            <SingleSubItemWrapper style={{ ...depthStylesMapping[depthLevel]?.li }} key={uuidv4()}>
              <Link href={`${buildRoute(routes.category, { id: item.id })}?mainCategory=${title}`}>
                <ListSubItemTitle>
                  {item.name} <ListSubItemCount>{totalCountExperts}</ListSubItemCount>
                </ListSubItemTitle>
              </Link>
            </SingleSubItemWrapper>

            {!!item.subCategories.length && renderChildrenMobile(item.subCategories, depthLevel + 1)}
          </>
        );
      })}
    </MobileUl>
  );

  return (
    <Container>
      <FirstRow>
        {/* <IconContainer>
          <Icon src={iconUrl} alt={title} />
        </IconContainer> */}
        {/* {href ? (
          <Title as="a" href={`${href}?mainCategory=${title}`}>
            {title}
          </Title>
        ) : (
          <Title $isUpcoming>{title}</Title>
        )} */}
        <Title $isUpcoming>{title}</Title>
      </FirstRow>
      <CountContainer>
        {chooseEndingWord(count, [
          t('experts.one', { count }),
          t('experts.two', { count }),
          t('experts.many', { count }),
        ])}
      </CountContainer>
      <Content>
        <ContentListWrapper>
          <List>
            {list.map((item) => (
              // isUpcoming ? (
              //   <ListItemWrapper $isUpcoming key={item.id}>
              //     {item.title}
              //   </ListItemWrapper>
              // ) : (
              <>
                <Link key={uuidv4()} href={`${item.href}?mainCategory=${title}`}>
                  <FirstLevelItem>
                    <SingleItemWrapper>
                      <FirstLevelItemTitle>{item.title}</FirstLevelItemTitle>
                      <FirstLevelItemCount>
                        {item.subCategories.length
                          ? item.count + getSubCategoriesCount(item.subCategories)
                          : item.count}
                      </FirstLevelItemCount>
                    </SingleItemWrapper>
                  </FirstLevelItem>
                </Link>
                {!!item.subCategories.length && renderChildren(item.subCategories, 3)}
              </>
            ))}
          </List>
        </ContentListWrapper>

        <ContentListWrapperMobile>
          <List>
            {list.map(
              (item) => (
                // isUpcoming ? (
                //   <ListItemWrapper $isUpcoming key={item.id}>
                //     {item.title}
                //   </ListItemWrapper>
                // ) : (
                <>
                  <SingleItemWrapper>
                    <Link key={uuidv4()} href={`${item.href}?mainCategory=${title}`}>
                      <ListItemTitle>
                        {item.title}{' '}
                        <ListItemCount>
                          {item.subCategories.length
                            ? item.count + getSubCategoriesCount(item.subCategories)
                            : item.count}
                        </ListItemCount>
                      </ListItemTitle>
                    </Link>
                  </SingleItemWrapper>

                  {!!item.subCategories.length && renderChildrenMobile(item.subCategories, 3)}
                </>
              ),
              // ),
            )}
          </List>
        </ContentListWrapperMobile>

        {/* {isUpcoming && <StyledBadge label={t('soon')} />} */}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  flex-direction: column;
  margin-bottom: 20px;
  ${CONDITION_DESKTOP} {
    width: 100%;
    display: flex;
  }

  ${CONDITION_MOBILE} {
    margin-bottom: 60px;
  }
`;

const Content = styled.div`
  flex-grow: 1;
  width: 100%;
  margin-left: 0;
`;
const Title = styled.h3<{ $isUpcoming?: boolean }>`
  font-weight: bold;
  font-size: 36px;
  overflow-wrap: break-word;
  color: #111111;
  :hover {
    color: ${({ $isUpcoming }) => ($isUpcoming ? undefined : '#8b33ff')};
  }

  ${CONDITION_MOBILE} {
    font-size: 32px;
    font-weight: bold;
  }
`;
const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0;

  a {
    -webkit-tap-highlight-color: transparent;
  }

  ${CONDITION_MOBILE} {
    gap: 0;
  }
`;

const FirstRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const ContentListWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 20px;

  ${CONDITION_MOBILE} {
    display: none;
  }
`;

const CountContainer = styled.div`
  color: #b1b1b1;
  font-size: 26px;
  font-weight: 500;
  line-height: 1.35;

  ${CONDITION_MOBILE} {
    margin-bottom: 30px;
  }
`;

const SingleItemWrapper = styled.li`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
  cursor: pointer;
  align-items: baseline;
  :hover {
    color: #8b33ff;
  }

  ${CONDITION_MOBILE} {
    margin-bottom: 20px;
  }
`;

const SingleSubItemWrapper = styled.li`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
  cursor: pointer;
  :hover {
    color: #8b33ff;
  }
  width: 100%;
`;

const ListItemTitle = styled.span`
  color: #111111;
  ${CONDITION_MOBILE} {
    font-size: 20px;
    font-weight: bold;
  }
  :hover {
    color: #8b33ff;
  }
`;

const ListItemCount = styled.span`
  color: #b1b1b1;
  margin-left: 8px;
  /* font-size: 19px; */
  font-weight: 500;
`;

const ListSubItemTitle = styled.span`
  ${CONDITION_MOBILE} {
    font-size: 16px;
    font-weight: 500;
  }
`;

const ListSubItemCount = styled.span`
  color: #b1b1b1;
  margin-left: 8px;
  font-size: 15px;
  font-weight: 500;
`;

const ContentListWrapperMobile = styled.div`
  display: flex;
  ${CONDITION_DESKTOP} {
    display: none;
  }
`;

const FirstLevelItem = styled.div`
  display: flex;
  flex-direction: row;
  /* margin-top: 60px; */
  &:first-child {
    margin-top: 40px;
  }
  margin-top: 10px;
`;

const FirstLevelItemTitle = styled.span`
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  color: #111111;

  :hover {
    color: #8b33ff;
  }
`;

const FirstLevelItemCount = styled.span`
  font-size: 19px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.05;
  letter-spacing: normal;
  color: rgba(17, 17, 17, 0.3);
  margin-left: 8px;
`;

const ListTitleWrapper = styled.div`
  :hover {
    color: #8b33ff;
  }
`;

const CategoriesList = styled.ul<{ $isLastLevel?: boolean }>`
  margin-top: 20px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: baseline;
  margin-bottom: ${({ $isLastLevel }) => ($isLastLevel ? '20px' : '60px')};
`;

const CategoriesListItem = styled.li`
  cursor: pointer;
`;

const MobileUl = styled.ul`
  row-gap: 15px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;
